import React, { useState, useRef, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import CalendarIcon from '@app/assets/icons/calendar-02.svg?react';
import clsx from 'clsx';
import { formatDateToISO } from '@app/utils/utils';

interface DateRangePickerProps {
  startName: string;
  endName: string;
  startPlaceholder?: string;
  endPlaceholder?: string;
  className?: string;
  containerClass?: string;
  position?: 'absolute' | 'fixed';
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startName,
  endName,
  startPlaceholder = "Start date",
  endPlaceholder = "End date",
  className = "",
  containerClass = "",
  position = "fixed",
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(null);
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(null);
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null);
  const [activeField, setActiveField] = useState<'start' | 'end' | null>(null);
  const [forceUpdate, setForceUpdate] = useState(0);
  
  const { setValue, watch } = useFormContext();
  const pickerRef = useRef<HTMLDivElement>(null);

  const startDate = watch(startName);
  const endDate = watch(endName);

  // Reset isOpen to false on mount
  useEffect(() => {
    setIsOpen(false);
    setActiveField(null);
  }, []);

  // Force re-render when selection changes
  useEffect(() => {
    // This effect ensures the component re-renders when selection changes
    setForceUpdate(prev => prev + 1);
  }, [selectedStartDate, selectedEndDate]);

  useEffect(() => {
    if (startDate && startDate.trim() !== '') {
      // Create date in local timezone to avoid timezone offset issues
      const [year, month, day] = startDate.split('-').map(Number);
      setSelectedStartDate(new Date(year, month - 1, day));
    } else {
      setSelectedStartDate(null);
    }
    if (endDate && endDate.trim() !== '') {
      // Create date in local timezone to avoid timezone offset issues
      const [year, month, day] = endDate.split('-').map(Number);
      setSelectedEndDate(new Date(year, month - 1, day));
    } else {
      setSelectedEndDate(null);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setActiveField(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatDate = (date: Date): string => {
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear();
    const currentYear = new Date().getFullYear();
    
    if (year === currentYear) {
      return `${day} ${month}`;
    }
    return `${day} ${month}, ${year}`;
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const isDateInRange = (date: Date) => {
    if (!selectedStartDate) return false;
    
    // If we're selecting the end date (activeField === 'end'), show range from start to hovered date
    if (activeField === 'end' && selectedStartDate && hoveredDate && 
        date.getTime() >= selectedStartDate.getTime() && date.getTime() <= hoveredDate.getTime()) {
      return true;
    }
    
    // If both dates are selected, show the actual range
    if (selectedStartDate && selectedEndDate) {
      return date.getTime() >= selectedStartDate.getTime() && date.getTime() <= selectedEndDate.getTime();
    }
    
    return false;
  };

  const isDateSelected = (date: Date) => {
    // Check against selectedStartDate (permanent state)
    if (selectedStartDate && 
        date.getFullYear() === selectedStartDate.getFullYear() &&
        date.getMonth() === selectedStartDate.getMonth() &&
        date.getDate() === selectedStartDate.getDate()) {
      return 'start';
    }
    
    // Check against selectedEndDate (permanent state)
    if (selectedEndDate && date.getFullYear() === selectedEndDate.getFullYear() && date.getMonth() === selectedEndDate.getMonth() && date.getDate() === selectedEndDate.getDate()) {
      return 'end';
    }
    
    return false;
  };

  const handleDateClick = (date: Date) => {
    if (activeField === 'start') {
      // Set permanent date immediately
      setSelectedStartDate(date);
      setValue(startName, formatDateToISO(date));
      setActiveField('end');
      // Force immediate re-render to ensure highlighting
      setForceUpdate(prev => prev + 1);
    } else if (activeField === 'end') {
      if (date >= selectedStartDate!) {
        // Set permanent date immediately
        setSelectedEndDate(date);
        setValue(endName, formatDateToISO(date));
        setIsOpen(false);
        setActiveField(null);
        // Force immediate re-render to ensure highlighting
        setForceUpdate(prev => prev + 1);
      } else {
        // If end date is before start date, swap them
        setSelectedEndDate(selectedStartDate);
        setSelectedStartDate(date);
        setValue(startName, formatDateToISO(date));
        setValue(endName, formatDateToISO(selectedStartDate!));
        setIsOpen(false);
        setActiveField(null);
        // Force immediate re-render to ensure highlighting
        setForceUpdate(prev => prev + 1);
      }
    }
  };

  const handleDateHover = (date: Date) => {
    if (activeField === 'end' && selectedStartDate && date >= selectedStartDate) {
      setHoveredDate(date);
    }
  };

  const clearDates = () => {
    setSelectedStartDate(null);
    setSelectedEndDate(null);
    setValue(startName, '');
    setValue(endName, '');
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const getRangeDays = () => {
    if (!selectedStartDate || !selectedEndDate) return 0;
    const diffTime = Math.abs(selectedEndDate.getTime() - selectedStartDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  const openPicker = (field: 'start' | 'end') => {
    setActiveField(field);
    setIsOpen(true);
  };

  const days = getDaysInMonth(currentMonth);
  const nextMonthDays = getDaysInMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  const monthName = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const nextMonthName = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className={`relative ${containerClass}`} ref={pickerRef}>
      <div className="flex items-center gap-2">
        {/* Start Date Field */}
        <div className="relative flex-shrink-0">
          <input
            type="text"
            readOnly
            placeholder={startPlaceholder}
            value={selectedStartDate ? formatDate(selectedStartDate) : ''}
            className={clsx(
              "ml-2 bg-gray-100 border-0 rounded-lg pl-10 pr-4 py-2 text-gray-600 placeholder:text-gray-500 focus:bg-gray-200 focus:outline-none min-w-[140px] cursor-pointer",
              className,
              { "bg-gray-200": activeField === 'start' }
            )}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              openPicker('start');
            }}
          />
          <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
            <CalendarIcon width={22} height={22} className="text-gray-500" />
          </div>
        </div>

        {/* Arrow Separator */}
        <div className="text-gray-400 flex-shrink-0">
          →
        </div>

        {/* End Date Field */}
        <div className="relative flex-shrink-0">
          <input
            type="text"
            readOnly
            placeholder={endPlaceholder}
            value={selectedEndDate ? formatDate(selectedEndDate) : ''}
            className={clsx(
              "bg-gray-100 border-0 rounded-lg px-4 py-2 text-gray-600 placeholder:text-gray-500 focus:bg-gray-200 focus:outline-none min-w-[140px] cursor-pointer",
              className,
              { "bg-gray-200": activeField === 'end' }
            )}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              openPicker('end');
            }}
          />
          {selectedEndDate && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                clearDates();
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Calendar Dropdown */}
      {isOpen && (
        <div className={`${position === 'absolute' ? 'absolute top-full left-0 mt-2' : 'fixed top-20 left-20'} bg-white border border-gray-200 rounded-lg shadow-lg z-[9999] p-6 min-w-[700px]`}>
          {/* Two Month Calendar Layout */}
          <div className="flex gap-8">
            {/* First Month */}
            <div className="flex-1">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">{monthName}</h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={prevMonth}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
                      <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <button
                    onClick={nextMonth}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
                      <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-7 gap-1">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                  <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                    {day}
                  </div>
                ))}
                {days.map((date, index) => {
                  const selectedType = date ? isDateSelected(date) : false;
                  const isSelected = selectedType === 'start' || selectedType === 'end';
                  const isInRange = date && isDateInRange(date) && !isSelected;
                  
                  return (
                    <div
                      key={`${index}-${selectedStartDate?.getTime()}-${selectedEndDate?.getTime()}-${forceUpdate}`}
                      className={clsx(
                        "text-center py-2 cursor-pointer text-sm rounded",
                        {
                          "text-gray-400": !date,
                          "hover:bg-blue-50": date && !isSelected && !isInRange,
                        }
                      )}
                      style={{
                        backgroundColor: isSelected ? '#52B6FE' : isInRange ? '#E3F2FD' : 'transparent',
                        color: isSelected ? 'white' : isInRange ? '#52B6FE' : undefined,
                
                        fontWeight: isSelected ? 'bold' : 'normal',
                      }}
                      onClick={() => date && handleDateClick(date)}
                      onMouseEnter={() => date && handleDateHover(date)}
                      onMouseLeave={() => setHoveredDate(null)}
                    >
                      {date ? date.getDate() : ''}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Second Month */}
            <div className="flex-1">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">{nextMonthName}</h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={prevMonth}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
                      <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <button
                    onClick={nextMonth}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
                      <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-7 gap-1">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                  <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                    {day}
                  </div>
                ))}
                {nextMonthDays.map((date, index) => {
                  const selectedType = date ? isDateSelected(date) : false;
                  const isSelected = selectedType === 'start' || selectedType === 'end';
                  const isInRange = date && isDateInRange(date) && !isSelected;
                  
                  return (
                    <div
                      key={`next-${index}-${selectedStartDate?.getTime()}-${selectedEndDate?.getTime()}-${forceUpdate}`}
                      className={clsx(
                        "text-center py-2 cursor-pointer text-sm rounded",
                        {
                          "text-gray-400": !date,
                          "hover:bg-blue-50": date && !isSelected && !isInRange,
                        }
                      )}
                      style={{
                        backgroundColor: isSelected ? '#52B6FE' : isInRange ? '#E3F2FD' : 'transparent',
                        color: isSelected ? 'white' : isInRange ? '#52B6FE' : undefined,

                        fontWeight: isSelected ? 'bold' : 'normal',
                      }}
                      onClick={() => date && handleDateClick(date)}
                      onMouseEnter={() => date && handleDateHover(date)}
                      onMouseLeave={() => setHoveredDate(null)}
                    >
                      {date ? date.getDate() : ''}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Range Summary */}
          {selectedStartDate && selectedEndDate && (
            <div className="text-sm text-gray-600 border-t pt-3 mt-4 text-center">
              {getRangeDays()} days: {formatDate(selectedStartDate)} - {formatDate(selectedEndDate)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
